/** Карточка книги: метаданные, продолжение чтения, скачивание файлов. */

import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api, coverUrl, downloadUrl } from '../api'
import type { BookDetail, RelatedGroup, ShelfStatus } from '../api'
import { ShelfControls } from '../components/ShelfControls'
import { Spinner } from '../components/Spinner'

export function BookPage() {
  const { id } = useParams()
  const bookId = Number(id)
  const navigate = useNavigate()
  const [book, setBook] = useState<BookDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<ShelfStatus>('none')
  const [favorite, setFavorite] = useState(false)
  const [related, setRelated] = useState<RelatedGroup[]>([])

  useEffect(() => {
    let cancelled = false
    api
      .book(bookId)
      .then((loaded) => !cancelled && setBook(loaded))
      .catch((exc) => !cancelled && setError(describe(exc)))
    // Отметки полки лежат отдельно от карточки: они у каждого свои.
    api
      .shelf({ limit: 200 })
      .then((page) => {
        if (cancelled) return
        const mine = page.items.find((b) => b.id === bookId)
        if (mine) {
          setStatus(mine.status)
          setFavorite(mine.favorite)
        }
      })
      .catch(() => {
        /* полка не обязательна для показа книги */
      })

    api
      .related(bookId)
      .then((data) => !cancelled && setRelated(data.groups))
      .catch(() => {
        /* без подборки карточка всё равно полна */
      })

    return () => {
      cancelled = true
    }
  }, [bookId])

  if (error) {
    return (
      <div className="center">
        <div>
          <p className="error">{error}</p>
          <Link className="btn" to="/">
            К каталогу
          </Link>
        </div>
      </div>
    )
  }
  if (!book) return <Spinner />

  const authors = book.authors.map((a) => a.name).join(', ')
  const reading = book.reading
  const percent = reading ? Math.round(reading.progress * 100) : 0

  return (
    <>
      <div className="topbar">
        <button className="btn btn--ghost" onClick={() => navigate(-1)}>
          ← Назад
        </button>
        <span className="topbar__spacer" />
      </div>

      <div className="content">
        <div className="book">
          <div className="card__cover">
            {book.has_cover ? (
              <img src={coverUrl(book.id)} alt="" />
            ) : (
              <div className="card__placeholder">{book.title}</div>
            )}
          </div>

          <div>
            <h1 className="book__title">{book.title}</h1>
            <div className="book__author">{authors || 'Автор неизвестен'}</div>

            <div className="book__actions">
              {book.readable ? (
                <Link className="btn btn--primary" to={`/read/${book.id}`}>
                  {reading && reading.progress > 0 ? `Продолжить · ${percent} %` : 'Читать'}
                </Link>
              ) : (
                <span className="dim">
                  Этот формат читается только во внешнем приложении
                </span>
              )}
              {book.files.map((file) => (
                <a key={file.id} className="btn" href={downloadUrl(file.id)}>
                  Скачать {file.format.toUpperCase()}
                  <span className="dim"> · {formatSize(file.size)}</span>
                </a>
              ))}
            </div>

            {reading && reading.max_progress > reading.progress + 0.01 && (
              <p className="dim">
                Максимально прочитано: {Math.round(reading.max_progress * 100)} % — на другом
                устройстве вы ушли дальше.
              </p>
            )}

            <ShelfControls
              bookId={book.id}
              status={status}
              favorite={favorite}
              onChange={(next, star) => {
                setStatus(next)
                setFavorite(star)
              }}
            />

            {book.annotation && <div className="book__annotation">{book.annotation}</div>}

            <dl className="meta">
              {book.series && (
                <>
                  <dt>Серия</dt>
                  <dd>
                    <Link to={`/?q=${encodeURIComponent(book.series.name)}`}>
                      {book.series.name}
                    </Link>
                    {book.series_index ? ` · ${book.series_index}` : ''}
                  </dd>
                </>
              )}
              {book.year && (
                <>
                  <dt>Год</dt>
                  <dd>{book.year}</dd>
                </>
              )}
              {book.lang && (
                <>
                  <dt>Язык</dt>
                  <dd>
                    {book.lang_name || book.lang}
                    {book.lang_guessed && (
                      <span className="dim" title="В метаданных языка нет — определён по тексту">
                        {' '}
                        · определён по тексту
                      </span>
                    )}
                  </dd>
                </>
              )}
              {book.genres.length > 0 && (
                <>
                  <dt>Жанры</dt>
                  <dd>{book.genres.map((g) => g.name).join(', ')}</dd>
                </>
              )}
              <dt>Форматы</dt>
              <dd>
                {book.formats.map((format) => (
                  <span key={format} className="badge" style={{ marginRight: '0.3rem' }}>
                    {format}
                  </span>
                ))}
              </dd>
            </dl>
          </div>
        </div>

        {related.map((group) => (
          <section key={group.title} className="related">
            <h3>{group.title}</h3>
            <div className="related__row">
              {group.items.map((item) => (
                <Link key={item.id} className="related__card" to={`/book/${item.id}`}>
                  {item.has_cover ? (
                    <img src={coverUrl(item.id)} alt="" loading="lazy" />
                  ) : (
                    <div className="card__placeholder">{item.title}</div>
                  )}
                  <div className="related__title">{item.title}</div>
                  <div className="dim">{item.authors.map((a) => a.name).join(', ')}</div>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </>
  )
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`
}

function describe(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message)
  }
  return 'Не удалось загрузить книгу'
}
